/**
 * The org hierarchy as a tree of credential counts, for the credentials sidebar.
 *
 * Distributor -> MSP -> Company, each node carrying how many credentials sit
 * beneath it and how many of those need someone. Pure, so the counting is
 * testable without a DOM.
 *
 * The tree *narrows*; it does not gate. Selecting nothing shows everything,
 * which is the difference between this and the Miller columns it replaces:
 * those rendered an empty page until you had clicked your way down to a
 * company, so a scope bar reading "Company A" could sit above a pane reading
 * "Pick a company" while the API had already returned the account.
 */
import type { ScopeHierarchy } from "@/hooks/useScopeHierarchy";
import type { Company } from "@/lib/api/companies";
import type { Credential } from "@/lib/api/credentials";
import type { Distributor } from "@/lib/api/distributors";
import type { MSP } from "@/lib/api/msps";

import { attentionState, needsAttention } from "./credentialHealth";

export type OrgNodeKind = "distributor" | "msp" | "company";

export interface OrgNode {
  kind: OrgNodeKind;
  id: string;
  label: string;
  /** Credentials at or below this node. */
  count: number;
  /** How many of those need a human. */
  attention: number;
  children: OrgNode[];
}

export interface OrgSelection {
  kind: OrgNodeKind;
  id: string;
}

const str = (v: unknown) => (v == null ? "" : String(v));

/**
 * Build the tree for a viewer.
 *
 * `showDistributors` comes from the same role signal the Organizations console
 * uses, so a role sees the same levels on both pages rather than from two sets
 * of rules that can disagree.
 *
 * Empty branches are kept: an MSP with no credentials yet is a real MSP, and
 * hiding it would make the sidebar disagree with the org console about what
 * exists.
 *
 * An MSP missing from `orgs.msps` is still built when a company names it. An
 * MSP admin is never sent the MSP list (their MSP is implicit, so the org hook
 * does not ask), and without this their tree came back empty and they got no
 * rail at all: the one viewer whose whole estate is a list of companies could
 * not pick one. Each company row carries its MSP's id and name, so the node is
 * built from what is already in hand rather than from another request.
 */
export function buildOrgTree(
  credentials: Credential[],
  orgs: { distributors: Distributor[]; msps: MSP[]; companies: Company[] },
  showDistributors: boolean,
  now: Date = new Date(),
): OrgNode[] {
  const credsByCompany = new Map<string, Credential[]>();
  for (const c of credentials) {
    const key = str(c.company);
    const list = credsByCompany.get(key);
    if (list) list.push(c);
    else credsByCompany.set(key, [c]);
  }

  const tally = (creds: Credential[]) => ({
    count: creds.length,
    attention: creds.filter((c) => needsAttention(attentionState(c, now)))
      .length,
  });

  const companyNode = (company: Company): OrgNode => {
    const creds = credsByCompany.get(str(company.id)) ?? [];
    return {
      kind: "company",
      id: str(company.id),
      label: company.name,
      ...tally(creds),
      children: [],
    };
  };

  const mspNode = (msp: Pick<MSP, "id" | "name">): OrgNode => {
    const children = orgs.companies
      .filter((c) => str(c.msp) === str(msp.id))
      .map(companyNode)
      .sort(byAttentionThenName);
    return {
      kind: "msp",
      id: str(msp.id),
      label: msp.name,
      count: children.reduce((n, c) => n + c.count, 0),
      attention: children.reduce((n, c) => n + c.attention, 0),
      children,
    };
  };

  const listed = new Set(orgs.msps.map((m) => str(m.id)));
  const implied = new Map<string, Pick<MSP, "id" | "name">>();
  for (const company of orgs.companies) {
    const id = str(company.msp);
    if (!id || listed.has(id) || implied.has(id)) continue;
    // "Unknown MSP", as the list's own group heading says for an MSP it
    // cannot name: a company you cannot place is still worth seeing.
    implied.set(id, { id, name: company.msp_name || "Unknown MSP" });
  }
  const mspNodes = [...orgs.msps, ...implied.values()].map(mspNode);

  if (!showDistributors) {
    return mspNodes.sort(byAttentionThenName);
  }

  const nodes = orgs.distributors
    .map((d) => {
      const children = mspNodes
        .filter((m) => {
          const msp = orgs.msps.find((x) => str(x.id) === m.id);
          return str(msp?.distributor) === str(d.id);
        })
        .sort(byAttentionThenName);
      return {
        kind: "distributor" as const,
        id: str(d.id),
        label: d.name,
        count: children.reduce((n, c) => n + c.count, 0),
        attention: children.reduce((n, c) => n + c.attention, 0),
        children,
      };
    })
    .sort(byAttentionThenName);

  // An MSP whose distributor is missing from the list (unassigned, or a
  // distributor the viewer cannot see) would otherwise vanish along with every
  // credential under it.
  const claimed = new Set(nodes.flatMap((d) => d.children.map((m) => m.id)));
  const orphans = mspNodes.filter((m) => !claimed.has(m.id));
  if (orphans.length) {
    nodes.push({
      kind: "distributor",
      id: "__unassigned__",
      label: "Unassigned",
      count: orphans.reduce((n, m) => n + m.count, 0),
      attention: orphans.reduce((n, m) => n + m.attention, 0),
      children: orphans.sort(byAttentionThenName),
    });
  }
  return nodes;
}

function byAttentionThenName(a: OrgNode, b: OrgNode): number {
  return (
    (b.attention > 0 ? 1 : 0) - (a.attention > 0 ? 1 : 0) ||
    a.label.localeCompare(b.label)
  );
}

/**
 * Company ids at or below a selection, or null for "everything".
 *
 * The page loads the viewer's whole branch and is narrowed here alone, so a
 * selection the tree cannot resolve narrows to nothing, never to everything.
 * Resolved to everything, the whole branch sat under a scope bar naming one
 * company, and Delete, Validate and Sync acted on rows outside it. A company
 * needs no tree: its own id is the narrowing. The list waits for the tree
 * before narrowing to an MSP or a distributor.
 */
export function companiesInSelection(
  tree: OrgNode[],
  selection: OrgSelection | null,
): Set<string> | null {
  if (!selection) return null;
  const found = findNode(tree, selection);
  if (!found) {
    return selection.kind === "company" ? new Set([selection.id]) : new Set();
  }
  const ids = new Set<string>();
  const walk = (node: OrgNode) => {
    if (node.kind === "company") ids.add(node.id);
    node.children.forEach(walk);
  };
  walk(found);
  return ids;
}

export function findNode(
  tree: OrgNode[],
  selection: OrgSelection,
): OrgNode | null {
  for (const node of tree) {
    if (node.kind === selection.kind && node.id === selection.id) return node;
    const hit = findNode(node.children, selection);
    if (hit) return hit;
  }
  return null;
}

/** Node ids that must be open for `selection` to be visible. */
export function pathToSelection(
  tree: OrgNode[],
  selection: OrgSelection | null,
): Set<string> {
  const open = new Set<string>();
  if (!selection) return open;
  const walk = (nodes: OrgNode[], trail: string[]): boolean => {
    for (const node of nodes) {
      const key = `${node.kind}:${node.id}`;
      if (node.kind === selection.kind && node.id === selection.id) {
        trail.forEach((t) => open.add(t));
        return true;
      }
      if (walk(node.children, [...trail, key])) return true;
    }
    return false;
  };
  walk(tree, []);
  return open;
}

type ScopeReading = Pick<
  ScopeHierarchy,
  | "selectedCompanyId"
  | "selectedMspId"
  | "selectedDistributorId"
  | "isMSPAdmin"
  | "isDistributor"
>;

/**
 * The rail's selection, read from the scope. There is no second copy: the
 * rail and the scope bar are two views of one answer to "which organization
 * are we looking at", and selecting on the rail *is* scoping to it, which is
 * what enables "Add cloud account" (a credential belongs to a company).
 *
 * A viewer's own organization reads as no selection. The scope reports an MSP
 * admin's MSP, and a distributor admin's distributor, even when nothing has
 * been picked, because that is where their estate starts; on the rail that is
 * "All accounts", not a narrowing. Reading it as a selection left "All
 * accounts" unhighlighted on a view that was, in fact, everything.
 */
export function selectionFromScope(scope: ScopeReading): OrgSelection | null {
  if (scope.selectedCompanyId) {
    return { kind: "company", id: String(scope.selectedCompanyId) };
  }
  if (scope.selectedMspId && !scope.isMSPAdmin) {
    return { kind: "msp", id: String(scope.selectedMspId) };
  }
  if (
    scope.selectedDistributorId &&
    !scope.isMSPAdmin &&
    !scope.isDistributor
  ) {
    return { kind: "distributor", id: String(scope.selectedDistributorId) };
  }
  return null;
}

/** Move the scope to a rail selection; `null` returns to the whole estate. */
export function applySelection(
  scope: Pick<
    ScopeHierarchy,
    "pickCompany" | "pickMsp" | "pickDistributor" | "clearScope"
  >,
  next: OrgSelection | null,
): void {
  if (!next) scope.clearScope();
  else if (next.kind === "company") scope.pickCompany(next.id);
  else if (next.kind === "msp") scope.pickMsp(next.id);
  else scope.pickDistributor(next.id);
}
